/**
 * Eurogastro — cascading product filter
 *
 * Filter 1: top-level "Produse" menu link        -> resolved instantly from embedded JSON (no fetch)
 * Filter 2: sub-link (subcollection) of Filter 1  -> resolved instantly from embedded JSON (no fetch)
 * Filter 3: "Tip"   native Shopify collection filter -> fetched live via Section Rendering API
 * Filter 4: "Model" native Shopify collection filter -> fetched live via Section Rendering API
 *
 * Requires: product metafields (e.g. custom.tip / custom.model) enabled as collection
 * filters in Shopify Admin > Search & Discovery > Filters, and this section embedded on
 * the collection template so the Section Rendering API fetch below has a `collection` object.
 */
(function () {
  'use strict';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[product-filter] could not parse JSON', e);
      return null;
    }
  }

  function setLoading(field, isLoading) {
    if (field) field.classList.toggle('is-loading', !!isLoading);
  }

  function resetSelect(select, placeholderText) {
    select.innerHTML = '';
    var opt = document.createElement('option');
    opt.value = '';
    opt.textContent = placeholderText || '';
    select.appendChild(opt);
    select.disabled = true;
  }

  function fillSelect(select, items, placeholderText) {
    resetSelect(select, placeholderText);
    items.forEach(function (item) {
      var opt = document.createElement('option');
      opt.value = item.value;
      opt.textContent = item.label;
      if (item.data) {
        Object.keys(item.data).forEach(function (k) {
          opt.dataset[k] = item.data[k];
        });
      }
      select.appendChild(opt);
    });
    select.disabled = items.length === 0;
  }

  function appendSectionParam(url, sectionId) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'section_id=' + encodeURIComponent(sectionId);
  }

  function fetchFiltersForUrl(url, sectionId) {
    var fetchUrl = appendSectionParam(url, sectionId);
    return fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Filter fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var dataEl = doc.querySelector('[data-pf-filters="' + sectionId + '"]');
        var data = readJSON(dataEl);
        return data || { collection_url: url, filters: [] };
      });
  }

  function findFilter(filters, matchTerm) {
    if (!filters || !matchTerm) return null;
    var term = matchTerm.toLowerCase();
    return (
      filters.find(function (f) {
        return (f.param_name || '').toLowerCase().indexOf(term) !== -1;
      }) ||
      filters.find(function (f) {
        return (f.label || '').toLowerCase().indexOf(term) !== -1;
      }) ||
      null
    );
  }

  function initFilterForm(form) {
    var sectionId = form.dataset.sectionId;
    var filter3Match = form.dataset.filter3Match || 'tip';
    var filter4Match = form.dataset.filter4Match || 'model';

    var treeEl = document.querySelector('[data-pf-tree="' + sectionId + '"]');
    var tree = readJSON(treeEl) || {};

    var select1 = qs(form, '[data-pf-select="1"]');
    var select2 = qs(form, '[data-pf-select="2"]');
    var select3 = qs(form, '[data-pf-select="3"]');
    var select4 = qs(form, '[data-pf-select="4"]');
    var submitBtn = qs(form, '[data-pf-submit]');

    var field2 = select2 ? select2.closest('.pf-field') : null;
    var field3 = select3 ? select3.closest('.pf-field') : null;
    var field4 = select4 ? select4.closest('.pf-field') : null;

    var placeholder2 = select2 ? select2.querySelector('option').textContent : '';
    var placeholder3 = select3 ? select3.querySelector('option').textContent : '';
    var placeholder4 = select4 ? select4.querySelector('option').textContent : '';

    var finalUrl = null;

    function lockFrom(level) {
      if (level <= 2 && select2) resetSelect(select2, placeholder2);
      if (level <= 3 && select3) resetSelect(select3, placeholder3);
      if (level <= 4 && select4) resetSelect(select4, placeholder4);
      finalUrl = null;
      if (submitBtn) submitBtn.disabled = true;
    }

    function loadFiltersForCollection(url) {
      setLoading(field3, true);
      return fetchFiltersForUrl(url, sectionId)
        .then(function (data) {
          finalUrl = data.collection_url || url;

          var tipFilter = findFilter(data.filters, filter3Match);
          var items = tipFilter
            ? tipFilter.values.map(function (v) {
                return { value: v.url_to_add, label: v.label + (v.count != null ? ' (' + v.count + ')' : ''), data: { urlToAdd: v.url_to_add } };
              })
            : [];

          if (select3) {
            fillSelect(select3, items, placeholder3);
          }

          // No Tip filter available: allow jumping straight to results.
          if (submitBtn) submitBtn.disabled = items.length > 0; // wait for a Tip/Model pick first if available
          if (!tipFilter && finalUrl && submitBtn) submitBtn.disabled = false;
        })
        .catch(function (err) {
          console.error('[product-filter]', err);
          if (select3) fillSelect(select3, [], placeholder3);
        })
        .finally(function () {
          setLoading(field3, false);
        });
    }

    function loadModelsForUrl(url) {
      setLoading(field4, true);
      return fetchFiltersForUrl(url, sectionId)
        .then(function (data) {
          finalUrl = data.collection_url || url;

          var modelFilter = findFilter(data.filters, filter4Match);
          var items = modelFilter
            ? modelFilter.values.map(function (v) {
                return { value: v.url_to_add, label: v.label + (v.count != null ? ' (' + v.count + ')' : ''), data: { urlToAdd: v.url_to_add } };
              })
            : [];

          if (select4) fillSelect(select4, items, placeholder4);
          if (submitBtn) submitBtn.disabled = false; // Tip alone is enough to search
        })
        .catch(function (err) {
          console.error('[product-filter]', err);
          if (select4) fillSelect(select4, [], placeholder4);
        })
        .finally(function () {
          setLoading(field4, false);
        });
    }

    // FILTER 1 changed -> populate FILTER 2 from the static tree, or skip straight to filters.
    if (select1) {
      select1.addEventListener('change', function () {
        lockFrom(2);
        var handle = select1.value;
        var node = tree[handle];
        if (!node) return;

        if (node.children && node.children.length) {
          var items = node.children.map(function (c) {
            return { value: c.url, label: c.title, data: { url: c.url } };
          });
          if (select2) fillSelect(select2, items, placeholder2);
        } else if (node.url) {
          // No subcollections: this menu item already points at a real collection.
          loadFiltersForCollection(node.url);
        }
      });
    }

    // FILTER 2 changed -> fetch Tip values for the chosen subcollection.
    if (select2) {
      select2.addEventListener('change', function () {
        lockFrom(3);
        var url = select2.value;
        if (url) loadFiltersForCollection(url);
      });
    }

    // FILTER 3 changed -> fetch Model values narrowed by the chosen Tip.
    if (select3) {
      select3.addEventListener('change', function () {
        lockFrom(4);
        var option = select3.options[select3.selectedIndex];
        var url = option && option.dataset.urlToAdd;
        finalUrl = url || finalUrl;
        if (submitBtn) submitBtn.disabled = !finalUrl;
        if (url) loadModelsForUrl(url);
      });
    }

    // FILTER 4 changed -> this is the most specific URL we have.
    if (select4) {
      select4.addEventListener('change', function () {
        var option = select4.options[select4.selectedIndex];
        var url = option && option.dataset.urlToAdd;
        if (url) finalUrl = url;
        if (submitBtn) submitBtn.disabled = !finalUrl;
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (finalUrl) {
        window.location.href = finalUrl;
      } else if (select2 && select2.value) {
        window.location.href = select2.value;
      } else if (select1 && select1.value) {
        var node = tree[select1.value];
        if (node && node.url) window.location.href = node.url;
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-product-filter]').forEach(initFilterForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when sections are added/changed in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();